import React, { useState } from 'react';
import styled from 'styled-components';
import { chatAnalytics } from '../../utils/analytics';

const FormBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9;
`;

const FormContainer = styled.div`
  padding: 12px 12px 20px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 8px 16px 16px 16px;
  border: 1px solid #e9ecef;
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 80px;
  z-index: 10;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const FormTitle = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 600;
`;



const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #495057;
  }
`;

const FormMessage = styled.p`
  margin: 0 0 12px 0;
  color: #6c757d;
  font-size: 13px;
  line-height: 1.3;
`;

const FormField = styled.div`
  margin-bottom: 10px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  color: #495057;
  font-size: 13px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #4A55A2;
    box-shadow: 0 0 0 2px rgba(74, 85, 162, 0.1);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
  background: #4A55A2;
  color: white;
  margin-top: 12px;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
  
  &:hover {
    background: #3d4785;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;



const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #4A55A2;
    box-shadow: 0 0 0 2px rgba(74, 85, 162, 0.1);
  }
`;

const ErrorText = styled.span`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

interface UserInfo {
  name: string;
  email: string;
  mobile: string;
  message: string;
}

interface UserInfoFormProps {
  onSubmit: (userInfo: UserInfo) => void;
  onClose: () => void;
  tenantcode: string;
  customMessage?: string;
  conversationHistory?: Array<{role: string; content: string}>;
}

export const UserInfoForm: React.FC<UserInfoFormProps> = ({ onSubmit, onClose, tenantcode, customMessage, conversationHistory }) => {
  const [formData, setFormData] = useState<UserInfo>({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<UserInfo>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track form view on mount
  React.useEffect(() => {
    chatAnalytics.trackFormView('user_info');
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<UserInfo> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    // Email is required (based on API error message)
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setErrors({}); // Clear previous errors
    
    try {
      const apiUrl = new URL('contact', import.meta.env.VITE_API_ENDPOINT_URL);
      const payload = {
        tenantcode,
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobilenumber: formData.mobile.trim() || undefined, // Optional field
        message: formData.message.trim(),
        conversationHistory: conversationHistory || []
      };
      
      console.log('Submitting form with payload:', payload);
      console.log('API endpoint:', apiUrl.toString());
      
      const response = await fetch(apiUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_CLIENT_JWT_SECRET}`,
          'Origin': window.location.origin,
        },
        body: JSON.stringify(payload)
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success) {
        // Success response
        console.log('Form submitted successfully:', responseData.message);
        onSubmit(formData);
      } else {
        // Error response
        const errorMessage = responseData.error || responseData.message || 'Submission failed';
        console.error('Form submission error:', errorMessage);
        
        // Show specific field errors if available
        if (errorMessage.includes('Missing required fields')) {
          setErrors({ 
            name: 'Please check all required fields are filled correctly' 
          });
        } else {
          setErrors({ 
            name: errorMessage 
          });
        }
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      setErrors({ 
        name: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof UserInfo, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <>
      <FormBackdrop />
      <FormContainer>
        <FormHeader>
          <FormTitle>Help us assist you better</FormTitle>
          <CloseButton onClick={onClose} type="button">
            ×
          </CloseButton>
        </FormHeader>
        <FormMessage>
          {customMessage || 'Please share your contact details and let us know how we can help you. We\'ll provide personalized support and follow up if needed.'}
        </FormMessage>
        
        <form onSubmit={handleSubmit}>
          <FormField>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter your name"
            />
            {errors.name && <ErrorText>{errors.name}</ErrorText>}
          </FormField>

          <FormField>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter your email"
            />
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FormField>

          <FormField>
            <Label htmlFor="mobile">Mobile</Label>
            <Input
              id="mobile"
              type="tel"
              value={formData.mobile}
              onChange={(e) => handleInputChange('mobile', e.target.value)}
              placeholder="Enter your mobile number (optional)"
            />
            {errors.mobile && <ErrorText>{errors.mobile}</ErrorText>}
          </FormField>

          <FormField>
            <Label htmlFor="message">Message *</Label>
            <TextArea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Please describe how we can help you or what information you're looking for..."
            />
            {errors.message && <ErrorText>{errors.message}</ErrorText>}
          </FormField>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </SubmitButton>
        </form>
      </FormContainer>
    </>
  );
};